import { Button, Dropdown, type MenuProps } from 'antd';
import {
  DownOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  HistoryOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useExportOrders, type OrderExportFilter } from './ExportOrderButton';

export interface OrderExcelButtonProps {
  filter: OrderExportFilter;
  /**
   * TCR-1626: "Lịch sử nhập" và "Nhập Excel" gate theo permission `orders.import`.
   */
  canImport: boolean;
  /**
   * TCR-1715 — "Xuất Excel" giờ gate theo `orders.export` (key MỚI, tách khỏi `orders.view`).
   * Trước đây mục này không gate gì: ai xem được danh sách đơn đều mang được toàn bộ dữ liệu
   * khách hàng ra ngoài.
   *
   * Cả `canImport` lẫn `canExport` đều false thì nút "Excel" ẩn hẳn — giữ lại một nút mở ra
   * dropdown rỗng thì tệ hơn là không có nút.
   */
  canExport: boolean;
  onOpenHistory: () => void;
  onOpenImport: () => void;
}

/**
 * TCR-1627: gộp 3 nút rời trên màn Bán hàng > Đơn hàng ("Lịch sử nhập", "Nhập từ file
 * Excel", "Xuất Excel") vào một nút "Excel" có dropdown, bám đúng mẫu
 * `ExcelProductButton` của màn Sản phẩm mà ticket đính kèm làm chuẩn.
 */
export function OrderExcelButton({
  filter,
  canImport,
  canExport,
  onOpenHistory,
  onOpenImport,
}: OrderExcelButtonProps) {
  const { exporting, exportOrders } = useExportOrders(filter);

  const items: MenuProps['items'] = [
    ...(canImport
      ? [
          {
            key: 'history',
            icon: <HistoryOutlined />,
            label: <span data-testid="orders-open-imports-history">Lịch sử nhập</span>,
          },
          {
            key: 'import',
            icon: <UploadOutlined />,
            label: <span data-testid="orders-open-import-modal">Nhập Excel</span>,
          },
        ]
      : []),
    ...(canExport
      ? [
          {
            key: 'export',
            icon: <DownloadOutlined />,
            label: <span data-testid="orders-export-excel">Xuất Excel</span>,
          },
        ]
      : []),
  ];

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'history') {
      onOpenHistory();
      return;
    }
    if (key === 'import') {
      onOpenImport();
      return;
    }
    if (key === 'export') {
      void exportOrders();
    }
  };

  // Không còn mục nào trong dropdown thì ẩn hẳn nút, không để người dùng bấm vào ô rỗng.
  if (items.length === 0) return null;

  return (
    <Dropdown menu={{ items, onClick: handleMenuClick }} placement="bottomRight" trigger={['click']}>
      {/* `aria-label` cứng: icon antd tự render `role="img" aria-label="file-excel"` và
          sẽ chui vào tên khả truy cập của nút ("file-excel Excel"), làm mọi locator
          `name: 'Excel'` trong test/e2e trượt một cách im lặng. */}
      <Button icon={<FileExcelOutlined />} loading={exporting} aria-label="Excel">
        Excel <DownOutlined />
      </Button>
    </Dropdown>
  );
}

export default OrderExcelButton;
